import api from './client';

// ---------------------------------------------------------------------------
// AI Status
// ---------------------------------------------------------------------------

export interface AIStatus {
  enabled: boolean;
}

export async function getAIStatus(): Promise<AIStatus> {
  const res = await api.get<AIStatus>('/ai/status');
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 1: Intelligent Version Diff
// ---------------------------------------------------------------------------

export interface DiffExplainRequest {
  session_id: string;
  version_a: number;
  version_b: number;
  diff_changes: Record<string, { old: unknown; new: unknown }>;
  ha?: number;
  ha_zi?: number;
  niveau?: number;
  language?: string;
}

export interface DiffExplainResponse {
  explanation: string;
  version_a: number;
  version_b: number;
  ai_generated: boolean;
}

export async function explainDiff(req: DiffExplainRequest): Promise<DiffExplainResponse> {
  const res = await api.post<DiffExplainResponse>('/ai/diff-explain', req);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 2: Anomaly Detection
// ---------------------------------------------------------------------------

export interface AnomalyItem {
  metric: string;
  severity: 'info' | 'warning' | 'critical';
  current_value: number;
  reference_mean: number;
  reference_std: number;
  z_score: number;
  explanation: string;
  recommendation: string;
}

export interface AnomalyCheckResponse {
  anomalies: AnomalyItem[];
  overall_status: 'ok' | 'warning' | 'critical';
  ai_generated: boolean;
}

export async function checkAnomalies(data: {
  estimation_data: Record<string, unknown>;
  session_id?: string;
  language?: string;
}): Promise<AnomalyCheckResponse> {
  const res = await api.post<AnomalyCheckResponse>('/ai/anomaly-check', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 3: Chat Assistant
// ---------------------------------------------------------------------------

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  reply: string;
  tool_calls: Record<string, unknown>[];
  ai_generated: boolean;
}

export async function sendChatMessage(data: {
  session_id: string;
  message: string;
  history?: ChatMessage[];
  ha?: number;
  ha_zi?: number;
  niveau?: number;
  current_results?: Record<string, unknown>;
}): Promise<ChatResponse> {
  const res = await api.post<ChatResponse>('/ai/chat', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 4: Document Extraction
// ---------------------------------------------------------------------------

export interface ExtractedField {
  value: string | number | null;
  confidence: number;
  source: string;
}

export interface DocumentExtractionResponse {
  fields: Record<string, ExtractedField>;
  document_type: string;
  page_count: number;
  ai_generated: boolean;
}

export async function extractDocument(file: File): Promise<DocumentExtractionResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<DocumentExtractionResponse>('/ai/extract-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 5: Report Narrative
// ---------------------------------------------------------------------------

export interface ReportSection {
  section: string;
  title: string;
  content: string;
}

export interface ReportNarrativeResponse {
  sections: ReportSection[];
  ai_generated: boolean;
}

export async function generateReportNarrative(data: {
  session_id: string;
  estimation_data: Record<string, unknown>;
  project_name?: string;
  ha?: number;
  ha_zi?: number;
  niveau?: number;
  sections?: string[];
  language?: string;
}): Promise<ReportNarrativeResponse> {
  const res = await api.post<ReportNarrativeResponse>('/ai/generate-report', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 6: Smart Ratio Recommendations
// ---------------------------------------------------------------------------

export interface RatioRecommendation {
  ratio_key: string;
  recommended_value: number;
  current_value: number;
  confidence: number;
  explanation: string;
  reference_projects: string[];
}

export interface RatioRecommendationResponse {
  recommendations: RatioRecommendation[];
  ai_generated: boolean;
}

export async function getSmartRatios(data: {
  ha?: number;
  ha_zi?: number;
  commune?: string;
  project_type?: string;
  current_ratios?: Record<string, number>;
}): Promise<RatioRecommendationResponse> {
  const res = await api.post<RatioRecommendationResponse>('/ai/recommend-ratios', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 7: Population Forecasting
// ---------------------------------------------------------------------------

export interface ForecastPoint {
  year: number;
  value: number;
  lower: number;
  upper: number;
}

export interface ForecastSeries {
  metric: string;
  label: string;
  unit: string;
  points: ForecastPoint[];
}

export interface PopulationForecastResponse {
  series: ForecastSeries[];
  milestones: Record<string, unknown>[];
  narrative: string;
  ai_generated: boolean;
}

export async function getForecast(data: {
  session_id: string;
  ha?: number;
  logements?: number;
  commune?: string;
  horizon_years?: number;
  ramp_years?: number;
}): Promise<PopulationForecastResponse> {
  const res = await api.post<PopulationForecastResponse>('/ai/forecast-population', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 8: Portfolio Optimization
// ---------------------------------------------------------------------------

export interface SharedInfrastructureItem {
  facility_type: string;
  projects: string[];
  potential_saving_chf: number;
  explanation: string;
}

export interface PortfolioAnalysisResponse {
  shared_infrastructure: SharedInfrastructureItem[];
  phasing_recommendations: Record<string, unknown>[];
  constraints: Record<string, unknown>[];
  total_potential_savings: number;
  narrative: string;
  ai_generated: boolean;
}

export async function analyzePortfolio(data: {
  project_ids: string[];
  language?: string;
}): Promise<PortfolioAnalysisResponse> {
  const res = await api.post<PortfolioAnalysisResponse>('/ai/portfolio-analyze', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 9: Spatial Analysis
// ---------------------------------------------------------------------------

export interface ProximityItem {
  facility_type: string;
  facility_name: string;
  distance_m: number;
  impact: string;
}

export interface SpatialAnalysisResponse {
  proximities: ProximityItem[];
  terrain_factors: Record<string, unknown>[];
  cost_impact_pct: number;
  narrative: string;
  ai_generated: boolean;
}

export async function analyzeSpatial(data: {
  geometry: Record<string, unknown>;
  commune?: string;
  project_id?: string;
}): Promise<SpatialAnalysisResponse> {
  const res = await api.post<SpatialAnalysisResponse>('/ai/spatial-analyze', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 10: Compliance Check
// ---------------------------------------------------------------------------

export interface ComplianceItem {
  parameter: string;
  status: 'compliant' | 'warning' | 'violation';
  current_value: number | string;
  limit_value: number | string;
  regulation_ref: string;
  explanation: string;
  suggestion: string;
}

export interface ComplianceCheckResponse {
  items: ComplianceItem[];
  overall_status: string;
  ai_generated: boolean;
}

export async function checkCompliance(data: {
  estimation_data: Record<string, unknown>;
  commune?: string;
  zone_type?: string;
  language?: string;
}): Promise<ComplianceCheckResponse> {
  const res = await api.post<ComplianceCheckResponse>('/ai/compliance-check', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 11: NL-to-Session (Natural Language Project Creation)
// ---------------------------------------------------------------------------

export interface ExtractedSessionParams {
  name: string;
  description?: string;
  ha: number;
  ha_zi?: number;
  commune?: string;
  niveau?: number;
  density_hint?: string;
  project_type?: string;
}

export interface NLSessionResponse {
  extracted_params: ExtractedSessionParams;
  ai_generated: boolean;
  confidence: number;
}

export async function createFromText(text: string, language?: string): Promise<NLSessionResponse> {
  const res = await api.post<NLSessionResponse>('/ai/create-from-text', { text, language });
  return res.data;
}

// ---------------------------------------------------------------------------
// Feature 12: Ratio Feedback (ML Data Collection)
// ---------------------------------------------------------------------------

export interface RatioFeedbackResponse {
  recorded: boolean;
  total_feedback_count: number;
}

export async function submitRatioFeedback(data: {
  session_id?: string;
  ratio_key: string;
  suggested_value: number;
  accepted_value: number;
  was_accepted?: boolean;
  ha?: number;
  commune?: string;
  project_type?: string;
}): Promise<RatioFeedbackResponse> {
  const res = await api.post<RatioFeedbackResponse>('/ai/ratio-feedback', data);
  return res.data;
}
